import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { installAuthInterceptor } from '../services/authInterceptor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Enable cookies on all axios requests
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sync Bearer header when token changes (backward compat for subscription/report APIs)
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // On mount: check session via httpOnly cookie
  const checkSession = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (err) {
      // No valid session — expected for logged-out visitors
      if (process.env.NODE_ENV !== 'production' && err?.response?.status !== 401) {
        console.warn('Session check failed unexpectedly:', err);
      }
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Install the 401 → refresh → retry interceptor exactly once.
  // Keeps users logged in for the full 7-day refresh window even though
  // access tokens roll over every 15 minutes.
  useEffect(() => {
    installAuthInterceptor({
      onAccessToken: (newToken) => setToken(newToken),
      onAuthFailure: () => {
        setUser(null);
        setToken(null);
        delete axios.defaults.headers.common['Authorization'];
        localStorage.removeItem('token');
        // Only redirect if we're on a protected page (avoid bouncing public pages)
        const path = window.location.pathname;
        const publicPaths = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/features', '/pricing', '/about', '/contact', '/tour', '/demo', '/brochure', '/video-recording', '/help', '/security', '/probate-checklist', '/executor-kit', '/public/estate', '/invite', '/share-story', '/attorney-unsubscribe'];
        const isPublic = publicPaths.some((p) => path === p || path.startsWith(p + '/'));
        if (!isPublic) {
          window.location.href = '/login';
        }
      },
    });
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, skip the /me check — AuthCallback will
    // exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // 2FA-enabled accounts get a challenge instead of a session.
    if (response.data.requires_2fa) {
      return { requires2fa: true, pendingToken: response.data.pending_token };
    }
    const { user: userData, access_token } = response.data;
    setToken(access_token);
    setUser(userData);
    return { requires2fa: false, user: userData };
  };

  const verifyTwoFactor = async (pendingToken, code) => {
    const response = await axios.post(`${API}/auth/2fa/verify-login`, {
      pending_token: pendingToken,
      code,
    });
    const { user: userData, access_token } = response.data;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const demoLogin = async () => {
    // One-click demo access. Backend re-seeds a rich estate on every call
    // so each session starts from a clean, judge-friendly dataset.
    const response = await axios.post(`${API}/auth/demo-login`);
    const { user: userData, access_token, estate_id } = response.data;
    setToken(access_token);
    setUser(userData);
    return { user: userData, estate_id };
  };

  const googleSession = async (sessionId) => {
    // Exchanges the one-time Emergent OAuth session_id for httpOnly auth cookies
    const response = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
    const { user: userData, access_token } = response.data;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { user: registeredUser, access_token } = response.data;
    setToken(access_token);
    setUser(registeredUser);
    return registeredUser;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Clear local state even if server call fails (e.g., network down)
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Server-side logout failed; clearing client state anyway:', err);
      }
    }
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    verifyTwoFactor,
    demoLogin,
    googleSession,
    register,
    logout,
    refreshSession: checkSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
